import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// API base URL - uses same origin in production
const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'https://localhost:3001';

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000,
  withCredentials: true, // Send cookies with requests
  headers: {
    'Content-Type': 'application/json'
  }
});

// CSRF token storage
let csrfToken: string | null = null;

/**
 * Gets CSRF token from cookie
 */
function getCSRFTokenFromCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf-token') {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Request interceptor to add CSRF token
 */
apiClient.interceptors.request.use(
  (config) => {
    // Get fresh CSRF token from cookie for each request
    csrfToken = getCSRFTokenFromCookie();
    
    if (csrfToken && config.method !== 'get') {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor to handle errors globally
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      // Handle authentication errors
      if (status === 401) {
        // Redirect to login if not already there
        if (window.location.pathname !== '/login' && window.location.pathname !== '/setup') {
          window.location.href = '/login';
        }
      }

      // Handle CSRF errors
      if (status === 403 && (data as any)?.error?.code === 'CSRF_TOKEN_INVALID') {
        // Refresh the page to get a new CSRF token
        window.location.reload();
      }

      // Handle request size limits (often returned by the frontend proxy)
      if (status === 413) {
        return Promise.reject({
          code: 'PAYLOAD_TOO_LARGE',
          message: 'CSV file is too large to import. Try a smaller file or increase the upload limit.'
        });
      }

      // Return the error data for handling by the caller
      if (typeof data === 'object' && data && (data as any).error) {
        return Promise.reject((data as any).error);
      }

      return Promise.reject({
        code: 'UNKNOWN',
        message: 'An error occurred'
      });
    }
    
    // Network or other errors
    return Promise.reject({
      code: 'NETWORK_ERROR',
      message: 'Unable to connect to server. Please check your connection.'
    });
  }
);

// ============================================
// API Response Types
// ============================================

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

// ============================================
// API Methods
// ============================================

/**
 * Generic GET request
 */
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response: AxiosResponse<ApiResponse<T>> = await apiClient.get(url, config);
  return response.data.data as T;
}

/**
 * Generic POST request
 */
export async function post<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response: AxiosResponse<ApiResponse<T>> = await apiClient.post(url, data, config);
  return response.data.data as T;
}

/**
 * Generic PUT request
 */
export async function put<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response: AxiosResponse<ApiResponse<T>> = await apiClient.put(url, data, config);
  return response.data.data as T;
}

/**
 * Generic PATCH request
 */
export async function patch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response: AxiosResponse<ApiResponse<T>> = await apiClient.patch(url, data, config);
  return response.data.data as T;
}

/**
 * Generic DELETE request
 */
export async function del<T = void>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response: AxiosResponse<ApiResponse<T>> = await apiClient.delete(url, config);
  return response.data.data as T;
}

/**
 * GET with pagination
 */
export async function getPaginated<T>(
  url: string,
  page: number = 1,
  limit: number = 50,
  config?: AxiosRequestConfig
): Promise<{ data: T[]; meta: ApiResponse['meta'] }> {
  const response: AxiosResponse<ApiResponse<T[]>> = await apiClient.get(url, {
    ...config,
    params: { ...config?.params, page, limit }
  });
  return {
    data: response.data.data || [],
    meta: response.data.meta
  };
}

// Export the axios instance for advanced use cases
export { apiClient };

// ============================================
// Backup & Import Types
// ============================================

export interface BackupMetadata {
  version: string;
  app_version: string;
  created_at: string;
  checksum: string;
  table_counts: {
    users: number;
    incomes: number;
    envelopes: number;
    transactions: number;
    debts: number;
    debt_payments: number;
    account_transactions: number;
  };
}

export interface BackupFile {
  metadata: BackupMetadata;
  data: {
    users: unknown[];
    incomes: unknown[];
    envelopes: unknown[];
    transactions: unknown[];
    debts: unknown[];
    debt_payments: unknown[];
    account_transactions: unknown[];
  };
}

export interface ImportResult {
  message: string;
  imported: {
    users?: number;
    incomes?: number;
    envelopes?: number;
    transactions?: number;
    debts?: number;
    debt_payments?: number;
    account_transactions?: number;
  };
  warnings?: string[];
}

// ============================================
// Backup & Import API Functions
// ============================================

/**
 * Export full backup as JSON
 */
export async function exportFullBackup(): Promise<BackupFile> {
  const response = await apiClient.get<BackupFile>('/export/backup');
  return response.data;
}

/**
 * Download full backup as a file
 */
export async function downloadBackup(): Promise<void> {
  const backup = await exportFullBackup();
  const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `budget-backup-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Import full backup (replaces all data)
 */
export async function importBackup(backup: BackupFile): Promise<ImportResult> {
  return post<ImportResult>('/import/backup', { backup });
}

/**
 * Download CSV export
 */
export async function downloadCsvExport(
  type: 'transactions' | 'envelopes' | 'incomes' | 'debts',
  params?: Record<string, string>
): Promise<void> {
  const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
  const response = await apiClient.get(`/export/${type}${queryString}`, {
    responseType: 'blob'
  });
  
  const blob = new Blob([response.data], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${type}-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Export default object with all methods
export default {
  get,
  post,
  put,
  patch,
  delete: del,
  getPaginated,
  client: apiClient,
  exportFullBackup,
  downloadBackup,
  importBackup,
  downloadCsvExport
};
